import React, { useState, useEffect, useRef } from "react";
import axios from "axios";

const API = "https://daily-report.spectratally.net/api";

import { useReactToPrint } from "react-to-print";
import "./DailyReport.css";
import { FaPrint } from "react-icons/fa";
import { RiRefreshLine } from "react-icons/ri";
import { MdDelete, MdAdd } from "react-icons/md";

// ── Default form state ──────────────────────────────────────────────────
const defaultForm = () => ({
  companyName  : "",
  date         : new Date().toISOString().slice(0, 10),
  department   : "",
  employeeName : "",

  // DSC Work Summary — now a dynamic array
  dscRows: [
    { totalDSC:"", signing:"", combo:"", dgft:"", other:"", companyName:"Prodigisign CA" },
    { totalDSC:"", signing:"", combo:"", dgft:"", other:"", companyName:"Capricorn CA"   },
  ],

  payments: [
    { paymentsReceived:"", totalAmount:"", mode:"Cash", dscDownloaded:"" },
  ],

  stock: [
    { location:"", openingStock:"", usedToday:"", closingStock:"" },
  ],

  customers: Array.from({ length: 5 }, () => ({
    customerName:"", orderNo:"", reference:"", amount:"", product:"", courier:"", tokenNo:"",
  })),

  tasks: [
    { expense:"Tea",     qty:"", unit:"", amount:"", remark:"" },
    { expense:"Water",   qty:"", unit:"", amount:"", remark:"" },
    { expense:"Courier", qty:"", unit:"", amount:"", remark:"" },
  ],

  visitLog: [
    { task:"", status:"" },
    { task:"", status:"" },
    { task:"", status:"" },
  ],

  preparedBy:"", checkedBy:"", signature:"",
});

// ── Migrate old dsc object → new dscRows array ───────────────────────────
function migrateDsc(data) {
  if (data.dscRows) return data; // already new format
  if (!data.dsc)    return { ...data, dscRows: defaultForm().dscRows };
  const d = data.dsc;
  return {
    ...data,
    dscRows: [
      { totalDSC:d.totalDSC||"", signing:d.signing||"", combo:d.combo||"", dgft:d.dgft||"", other:d.other||"", companyName:"Prodigisign CA" },
      { totalDSC:d.totalDSC2||"", signing:d.signing2||"", combo:d.combo2||"", dgft:d.dgft2||"", other:d.other2||"", companyName:"Capricorn CA" },
    ],
  };
}

// ── Main component ───────────────────────────────────────────────────────
export default function DailyReport({ editReport, onSaved }) {
  const [form, setForm] = useState(defaultForm());
  const [saving, setSaving] = useState(false);
  const [msg, setMsg]     = useState(null);
  const printRef = useRef();

  useEffect(() => {
    if (editReport) {
      setForm({ ...defaultForm(), ...migrateDsc(editReport) });
    } else {
      setForm(defaultForm());
    }
  }, [editReport]);

  const handlePrint = useReactToPrint({ content: () => printRef.current });

  // ── Generic updaters ──
  const setHeader = (field, val) => setForm(f => ({ ...f, [field]: val }));

  const setRow = (section, idx, field, val) =>
    setForm(f => {
      const arr = [...f[section]];
      arr[idx] = { ...arr[idx], [field]: val };
      return { ...f, [section]: arr };
    });

  const addRow = (section, template) =>
    setForm(f => ({ ...f, [section]: [...f[section], { ...template }] }));

  const removeRow = (section, idx) =>
    setForm(f => ({ ...f, [section]: f[section].filter((_, i) => i !== idx) }));

  // Auto-calc closing stock
  const handleStock = (idx, field, val) => {
    setForm(f => {
      const arr = [...f.stock];
      const row = { ...arr[idx], [field]: val };
      const op  = parseFloat(row.openingStock) || 0;
      const used= parseFloat(row.usedToday)    || 0;
      row.closingStock = (op - used).toString();
      arr[idx] = row;
      return { ...f, stock: arr };
    });
  };

  // ── Submit ──
  const handleSubmit = async (e) => {
    e.preventDefault(); setSaving(true); setMsg(null);
    try {
      if (editReport?.id) {
        await axios.put(`${API}/reports/${editReport.id}`, form);
        setMsg({ type:"success", text:"Report updated successfully!" });
      } else {
        await axios.post(`${API}/reports`, form);
        setMsg({ type:"success", text:"Report saved successfully!" });
        setForm(defaultForm());
      }
      setTimeout(onSaved, 1500);
    } catch {
      setMsg({ type:"error", text:"Failed to save report. Is the backend running?" });
    } finally { setSaving(false); }
  };

  const handleReset = () => { if (window.confirm("Reset all fields?")) setForm(defaultForm()); };

  return (
    <div className="report-container">
      {/* Action bar */}
      <div className="action-bar no-print">
        <h2 className="page-title">
          {editReport ? "Edit Report" : "New Daily Report"}
        </h2>
        <div className="action-btns">
          <button className="btn btn-outline" onClick={handlePrint} type="button"><FaPrint /> Print / PDF</button>
          <button className="btn btn-ghost"   onClick={handleReset} type="button"><RiRefreshLine /> Reset</button>
        </div>
      </div>

      {msg && <div className={`alert alert-${msg.type} no-print`}>{msg.text}</div>}

      <form onSubmit={handleSubmit}>
        <div className="report-card print-area" ref={printRef}>

          {/* HEADER */}
          <div className="report-header-title">DAILY OFFICE WORK REPORT</div>
          <div className="header-grid">
            <Field label="Company Name"  value={form.companyName}  onChange={v=>setHeader("companyName",v)} />
            <Field label="Date"          type="date" value={form.date} onChange={v=>setHeader("date",v)} />
            <Field label="Employee Name" value={form.employeeName} onChange={v=>setHeader("employeeName",v)} />
            <Field label="Department"    value={form.department}   onChange={v=>setHeader("department",v)} />
          </div>

          <Divider />

          {/* DSC WORK SUMMARY — dynamic rows */}
          <SectionTitle>DSC WORK SUMMARY</SectionTitle>
          <div className="tbl-wrapper">
            <table className="report-table dsc-table">
              <thead>
                <tr>
                  <Th>Total DSC</Th>
                  <Th>Signing</Th>
                  <Th>Combo</Th>
                  <Th>DGFT</Th>
                  <Th>Other</Th>
                  <Th>Company Name</Th>
                  <Th className="no-print">Action</Th>
                </tr>
              </thead>
              <tbody>
                {form.dscRows.map((row, i) => (
                  <tr key={i}>
                    <Td><input className="tbl-input" type="number" value={row.totalDSC}    onChange={e=>setRow("dscRows",i,"totalDSC",e.target.value)} /></Td>
                    <Td><input className="tbl-input" type="number" value={row.signing}     onChange={e=>setRow("dscRows",i,"signing",e.target.value)} /></Td>
                    <Td><input className="tbl-input" type="number" value={row.combo}       onChange={e=>setRow("dscRows",i,"combo",e.target.value)} /></Td>
                    <Td><input className="tbl-input" type="number" value={row.dgft}        onChange={e=>setRow("dscRows",i,"dgft",e.target.value)} /></Td>
                    <Td><input className="tbl-input" type="number" value={row.other}       onChange={e=>setRow("dscRows",i,"other",e.target.value)} /></Td>
                    <Td><input className="tbl-input" value={row.companyName} onChange={e=>setRow("dscRows",i,"companyName",e.target.value)} /></Td>
                    <Td className="no-print">
                      <button type="button" className="icon-btn danger" onClick={()=>removeRow("dscRows",i)} title="Remove row">
                        <MdDelete />
                      </button>
                    </Td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <button type="button" className="add-row-btn no-print"
            onClick={()=>addRow("dscRows",{totalDSC:"",signing:"",combo:"",dgft:"",other:"",companyName:""})}>
            <MdAdd /> Add DSC Row
          </button>

          <Divider />

          {/* PAYMENT DETAILS */}
          <SectionTitle>PAYMENT DETAILS</SectionTitle>
          <div className="tbl-wrapper">
            <table className="report-table payment-table">
              <thead>
                <tr>
                  <Th>S.No.</Th>
                  <Th>Payments Received</Th>
                  <Th>Total Amount (₹)</Th>
                  <Th>Mode</Th>
                  <Th>DSC Downloaded</Th>
                  <Th className="no-print">Action</Th>
                </tr>
              </thead>
              <tbody>
                {form.payments.map((p, i) => (
                  <tr key={i}>
                    <Td style={{textAlign:"center",fontWeight:600,width:"45px"}}>{i+1}</Td>
                    <Td><input className="tbl-input" type="number" value={p.paymentsReceived} onChange={e=>setRow("payments",i,"paymentsReceived",e.target.value)} /></Td>
                    <Td><input className="tbl-input" type="number" value={p.totalAmount}      onChange={e=>setRow("payments",i,"totalAmount",e.target.value)} /></Td>
                    <Td>
                      <select className="tbl-input" value={p.mode} onChange={e=>setRow("payments",i,"mode",e.target.value)}>
                        <option>Cash</option><option>UPI</option><option>Other</option>
                      </select>
                    </Td>
                    <Td><input className="tbl-input" type="number" value={p.dscDownloaded} onChange={e=>setRow("payments",i,"dscDownloaded",e.target.value)} /></Td>
                    <Td className="no-print">
                      <button type="button" className="icon-btn danger" onClick={()=>removeRow("payments",i)} title="Remove"><MdDelete /></button>
                    </Td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <button type="button" className="add-row-btn no-print"
            onClick={()=>addRow("payments",{paymentsReceived:"",totalAmount:"",mode:"Cash",dscDownloaded:""})}>
            <MdAdd /> Add Payment Row
          </button>

          <Divider />

          {/* DSC STOCK DETAILS */}
          <SectionTitle>DSC STOCK DETAILS</SectionTitle>
          <TableWrapper>
            <thead>
              <tr>
                <Th>Location</Th>
                <Th>Opening Stock</Th>
                <Th>Used Today</Th>
                <Th>Closing Stock</Th>
                <Th className="no-print">Action</Th>
              </tr>
            </thead>
            <tbody>
              {form.stock.map((row, i) => (
                <tr key={i}>
                  <Td><input className="tbl-input" value={row.location}     onChange={e=>setRow("stock",i,"location",e.target.value)} /></Td>
                  <Td><input className="tbl-input" type="number" value={row.openingStock} onChange={e=>handleStock(i,"openingStock",e.target.value)} /></Td>
                  <Td><input className="tbl-input" type="number" value={row.usedToday}    onChange={e=>handleStock(i,"usedToday",e.target.value)} /></Td>
                  <Td><input className="tbl-input" value={row.closingStock} readOnly style={{background:"#f8faff"}} /></Td>
                  <Td className="no-print">
                    <button type="button" className="icon-btn danger" onClick={()=>removeRow("stock",i)} title="Remove"><MdDelete /></button>
                  </Td>
                </tr>
              ))}
            </tbody>
          </TableWrapper>
          <button type="button" className="add-row-btn no-print"
            onClick={()=>addRow("stock",{location:"",openingStock:"",usedToday:"",closingStock:""})}>
            <MdAdd /> Add Stock Row
          </button>

          <Divider />

          {/* CUSTOMER / ORDER DETAILS */}
          <SectionTitle>CUSTOMER / ORDER DETAILS</SectionTitle>
          <div className="table-scroll">
            <TableWrapper>
              <thead>
                <tr>
                  <Th>S.No.</Th><Th>Customer Name</Th><Th>Order No.</Th>
                  <Th>Reference</Th><Th>Amount (₹)</Th><Th>Product</Th>
                  <Th>Courier</Th><Th>Token No.</Th>
                  <Th className="no-print">Action</Th>
                </tr>
              </thead>
              <tbody>
                {form.customers.map((c, i) => (
                  <tr key={i}>
                    <Td style={{textAlign:"center",fontWeight:600}}>{i+1}</Td>
                    <Td><input className="tbl-input" value={c.customerName} onChange={e=>setRow("customers",i,"customerName",e.target.value)} /></Td>
                    <Td><input className="tbl-input" value={c.orderNo}      onChange={e=>setRow("customers",i,"orderNo",e.target.value)} /></Td>
                    <Td><input className="tbl-input" value={c.reference}    onChange={e=>setRow("customers",i,"reference",e.target.value)} /></Td>
                    <Td><input className="tbl-input" type="number" value={c.amount} onChange={e=>setRow("customers",i,"amount",e.target.value)} /></Td>
                    <Td><input className="tbl-input" value={c.product}      onChange={e=>setRow("customers",i,"product",e.target.value)} /></Td>
                    <Td><input className="tbl-input" value={c.courier}      onChange={e=>setRow("customers",i,"courier",e.target.value)} /></Td>
                    <Td><input className="tbl-input" value={c.tokenNo}      onChange={e=>setRow("customers",i,"tokenNo",e.target.value)} /></Td>
                    <Td className="no-print">
                      <button type="button" className="icon-btn danger" onClick={()=>removeRow("customers",i)} title="Remove"><MdDelete /></button>
                    </Td>
                  </tr>
                ))}
              </tbody>
            </TableWrapper>
          </div>
          <button type="button" className="add-row-btn no-print"
            onClick={()=>addRow("customers",{customerName:"",orderNo:"",reference:"",amount:"",product:"",courier:"",tokenNo:""})}>
            <MdAdd /> Add Customer Row
          </button>

          <Divider />

          {/* ADDITIONAL TASKS / NOTES */}
          <SectionTitle>ADDITIONAL TASKS / NOTES</SectionTitle>
          <TableWrapper>
            <thead>
              <tr>
                <Th>S.No.</Th><Th>Expense</Th><Th>Qty</Th>
                <Th>Unit</Th><Th>Amount (₹)</Th><Th>Remark</Th>
                <Th className="no-print">Action</Th>
              </tr>
            </thead>
            <tbody>
              {form.tasks.map((t, i) => (
                <tr key={i}>
                  <Td style={{textAlign:"center",fontWeight:600}}>{i+1}</Td>
                  <Td><input className="tbl-input" value={t.expense} onChange={e=>setRow("tasks",i,"expense",e.target.value)} /></Td>
                  <Td><input className="tbl-input" type="number" value={t.qty}    onChange={e=>setRow("tasks",i,"qty",e.target.value)} /></Td>
                  <Td><input className="tbl-input" value={t.unit}    onChange={e=>setRow("tasks",i,"unit",e.target.value)} /></Td>
                  <Td><input className="tbl-input" type="number" value={t.amount} onChange={e=>setRow("tasks",i,"amount",e.target.value)} /></Td>
                  <Td><input className="tbl-input" value={t.remark}  onChange={e=>setRow("tasks",i,"remark",e.target.value)} /></Td>
                  <Td className="no-print">
                    <button type="button" className="icon-btn danger" onClick={()=>removeRow("tasks",i)} title="Remove"><MdDelete /></button>
                  </Td>
                </tr>
              ))}
            </tbody>
          </TableWrapper>
          <button type="button" className="add-row-btn no-print"
            onClick={()=>addRow("tasks",{expense:"",qty:"",unit:"",amount:"",remark:""})}>
            <MdAdd /> Add Task Row
          </button>

          <Divider />

          {/* VISIT / TASK LOG */}
          <SectionTitle>VISIT / TASK LOG</SectionTitle>
          <TableWrapper>
            <thead>
              <tr>
                <Th>S.No.</Th><Th>Task / Description</Th><Th>Status</Th>
                <Th className="no-print">Action</Th>
              </tr>
            </thead>
            <tbody>
              {form.visitLog.map((v, i) => (
                <tr key={i}>
                  <Td style={{textAlign:"center",fontWeight:600}}>{i+1}</Td>
                  <Td><input className="tbl-input" value={v.task} onChange={e=>setRow("visitLog",i,"task",e.target.value)} /></Td>
                  <Td>
                    <select className="tbl-input" value={v.status} onChange={e=>setRow("visitLog",i,"status",e.target.value)}>
                      <option value="">-- Select --</option>
                      <option>Completed</option><option>Pending</option>
                      <option>In Progress</option><option>Cancelled</option>
                    </select>
                  </Td>
                  <Td className="no-print">
                    <button type="button" className="icon-btn danger" onClick={()=>removeRow("visitLog",i)} title="Remove"><MdDelete /></button>
                  </Td>
                </tr>
              ))}
            </tbody>
          </TableWrapper>
          <button type="button" className="add-row-btn no-print"
            onClick={()=>addRow("visitLog",{task:"",status:""})}>
            <MdAdd /> Add Log Row
          </button>

          <Divider />

          {/* FOOTER SIGNATURES */}
          <div className="signature-grid">
            <Field label="Prepared By" value={form.preparedBy} onChange={v=>setHeader("preparedBy",v)} />
            <Field label="Checked By"  value={form.checkedBy}  onChange={v=>setHeader("checkedBy",v)} />
            <Field label="Signature"   value={form.signature}  onChange={v=>setHeader("signature",v)} />
          </div>

        </div>{/* end report-card */}

        <div className="submit-bar no-print">
          <button type="submit" className="btn btn-primary" disabled={saving}>
            {saving ? "Saving..." : editReport ? "Update Report" : "Save Report"}
          </button>
        </div>
      </form>
    </div>
  );
}

function SectionTitle({ children }) { return <div className="section-title">{children}</div>; }
function Divider() { return <hr className="section-divider" />; }

function Field({ label, value, onChange, type="text" }) {
  return (
    <div className="field-group">
      <label className="field-label">{label}</label>
      <input className="field-input" type={type} value={value} onChange={e=>onChange(e.target.value)} />
    </div>
  );
}

function TableWrapper({ children }) {
  return (
    <div className="tbl-wrapper">
      <table className="report-table">{children}</table>
    </div>
  );
}

function Th({ children, className, style }) {
  return <th className={`tbl-th ${className||""}`} style={style}>{children}</th>;
}

function Td({ children, className, style }) {
  return <td className={`tbl-td ${className||""}`} style={style}>{children}</td>;
}
